import React, { FC, useCallback, useContext, useMemo, useState } from 'react';

import { createEditor } from 'slate';
import { Editable, RenderLeafProps, Slate, withReact } from 'slate-react';

import FormulaEditorToolbar, {
    defaultToolbarControls,
    emailToolbarControls,
    FormulaEditorToolbarControls,
    pushToolbarControls,
} from '../../views/formulaCreate/formulaEditor/assistant/components/FormulaEditorToolbar';
import DeeplinkComponentV2 from '../PushNotificationEditor/Editor/components/Deeplink/DeeplinkComponentV2';
import ImageInputElement from '../PushNotificationEditor/Editor/components/Image/ImageInputElement';
import AttributePlaceholderElementComponent from '../PushNotificationEditor/Editor/components/SlateInput/AttributePlaceholder/AttributePlaceholderElementComponent';
// components
import SlateInputElement from '../PushNotificationEditor/Editor/components/SlateInput/SlateInputElement';
import SurfaceButtonElement from '../PushNotificationEditor/Editor/components/SurfaceButtonElement';
import CMSPlaceholderViewElement from './components/CMSPlaceholderViewElement';
import DatasetElement from './components/DatasetElement';
import Leaf from './components/Leaf';
import LinkElementView from './components/LinkElementView';
import VariantElement from './components/VariantElement';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';

// models
import { EditorContext } from '../../context/EditorContext';

// helper
import { showImageAlternates } from '../../helpers/formulaHelpers';

import { ElementKind } from '../../models/editor.model';
import {
    MaxCharacterModel,
    ProcessedFormulaV1,
} from '../../models/formula.model';
import { SurfaceButtonFieldLabel } from '../../models/surface.model';

interface FormulaViewerProps {
    formula: ProcessedFormulaV1;
    handleSelection?: (e: Record<string, any> | null) => void;
    selectedComponent: Record<string, any> | null;
    isReadOnly?: boolean;
    maxCharacterCounts?: MaxCharacterModel;
    className?: string;
    isEditable?: boolean;
}

const FormulaViewer: FC<React.PropsWithChildren<FormulaViewerProps>> = ({
    formula,
    handleSelection,
    selectedComponent,
    isReadOnly = false,
    maxCharacterCounts,
    className,
    isEditable,
}) => {
    const [editor] = useState(() => withReact(createEditor()));

    const {
        editorDetails: { isApproved, formulaType },
    } = useContext(EditorContext);

    const checkAndRenderImageAlternates = useCallback<
        (element: any) => boolean
    >(
        (element: any) =>
            formulaType ? showImageAlternates(formulaType, element) : false,
        [formulaType],
    );

    const renderElement = useCallback(
        (props: any) => {
            props.formulaId = formula.id;
            const surfaceFieldLabel =
                props.element?.surfaceFieldLabel ??
                (props.element?.surfaceFieldName ===
                SurfaceButtonFieldLabel.Text
                    ? 'text'
                    : props.element?.surfaceFieldName);
            switch (props.element.kind) {
                case ElementKind.section:
                case ElementKind.buttonAction:
                case ElementKind.buttonText:
                case ElementKind.image:
                    if (checkAndRenderImageAlternates(props.element)) {
                        return (
                            <ImageInputElement
                                isDisabled={
                                    isReadOnly || isApproved || !isEditable
                                }
                                selectedElement={selectedComponent}
                                handleUpdateStructure={() => null}
                                label={surfaceFieldLabel}
                                {...props}
                            />
                        );
                    }
                    return (
                        <SlateInputElement
                            insertEmoji={() => null}
                            insertDataset={() => null}
                            insertCMSPlaceholder={() => null}
                            {...props}
                            readOnly={isReadOnly || isApproved || !isEditable}
                            maxCharacterCounts={maxCharacterCounts}
                            label={surfaceFieldLabel}
                        />
                    );
                case ElementKind.variant:
                    return (
                        <VariantElement
                            {...props}
                            handleSelection={handleSelection}
                            selectedComponent={selectedComponent}
                        />
                    );
                case ElementKind.dataset:
                    return (
                        <DatasetElement
                            {...props}
                            handleSelection={handleSelection}
                            selectedComponent={selectedComponent}
                        />
                    );
                case ElementKind.cms_placeholder:
                    return (
                        <CMSPlaceholderViewElement
                            {...props}
                            handleSelection={handleSelection}
                        />
                    );
                case ElementKind.attributePlaceholder:
                    return <AttributePlaceholderElementComponent {...props} />;
                case ElementKind.link:
                    return (
                        <LinkElementView
                            {...props}
                            selectedElement={selectedComponent}
                            handleSelection={handleSelection}
                        />
                    );
                case ElementKind.surfaceButton:
                    return (
                        <SurfaceButtonElement
                            label={props.element?.surfaceFieldLabel}
                            {...props}
                        />
                    );
                default:
                    return (
                        <div {...props.attributes}>
                            <span contentEditable={false}>ERROR</span>
                            {props.children}
                        </div>
                    );
            }
        },
        [
            formula.id,
            checkAndRenderImageAlternates,
            isReadOnly,
            isApproved,
            maxCharacterCounts,
            handleSelection,
            selectedComponent,
            isEditable,
        ],
    );

    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        [],
    );

    const toolbarControls = useMemo<FormulaEditorToolbarControls[]>(() => {
        if (formulaType === FormulaType.email) {
            return emailToolbarControls;
        }
        if (formulaType === FormulaType.mobilePush) {
            return pushToolbarControls;
        }
        return defaultToolbarControls;
    }, [formulaType]);

    return (
        <Container className={className}>
            <Content>
                <Slate
                    editor={editor}
                    initialValue={formula.structure ?? []}
                    onChange={() => null}
                >
                    <Editable
                        style={{
                            borderRadius: aampeTheme.borderRadius.default,
                            backgroundColor: aampeTheme.colors.backgroundWG02,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: aampeTheme.spacing4(4),
                            outline: 'none',
                        }}
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        readOnly={true}
                    />
                </Slate>

                {formulaType && (
                    <DeeplinkComponentV2 isDisabled={!isEditable} />
                )}
            </Content>
            <FormulaEditorToolbar
                isEditable={isEditable!}
                handleUpdateStructure={() => null}
                toolbarControls={toolbarControls}
                formulaId={formula.id}
                hasWriteAccess={formula.has_write_access}
            />
        </Container>
    );
};

const Container = styled.div(() => ({
    width: '50%',
    borderRadius: aampeTheme.borderRadius.default,
}));
const Content = styled.div({
    width: '100%',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(6),
});

export default FormulaViewer;
